import type { CommandHandler } from '../types';
import { GridType } from '../../../menu/types';
import { isBooleanValueTrue } from '../../utils/booleanFieldUtils';

/**
 * Handler for 'add' command
 * Creates a new row in inline-editable grids or navigates to add form if detail form is configured
 */
export const addCommandHandler: CommandHandler = (_command, context) => {
  const { metadata, navigate, currentPath, onAddRow } = context;

  // Only defer to onAddRow for inline-editable grids — where it actually
  // adds a new row. For detail-form grids, onAddRow is a no-op placeholder,
  // and we must fall through to navigate() instead.
  if (onAddRow && metadata.gridType !== GridType.GridWithDetail) {
    onAddRow();
    return { success: true };
  }

  const hasDetailForm = metadata.detailFormId != null && metadata.detailFormKey != null;

  if (hasDetailForm) {
    const cleanPathname = currentPath.endsWith('/') ? currentPath.slice(0, -1) : currentPath;

    navigate(`${cleanPathname}/add`, {
      search: { gridContentKey: metadata.contentKey },
    });

    return { success: true };
  }

  // Child grid in a detail form — caller must provide onAddRow.
  if (onAddRow) {
    onAddRow();
    return { success: true };
  }

  return {
    success: false,
    error: 'Add row handler not available',
  };
};

/**
 * Handler for 'edit' command
 * Enables inline editing mode or navigates to edit form
 */
export const editCommandHandler: CommandHandler = (command, context) => {
  const { metadata, selectedRows, displayData, onEditRow } = context;

  const hasDetailForm = metadata.detailFormId != null && metadata.detailFormKey != null;
  const isInlineEdit = command.isInlineEdit;

  // Navigate to detail form if:
  // - gridType is GridWithDetail, OR both detailFormId and detailFormKey are configured
  // - AND the command is NOT marked as inline edit
  if ((metadata.gridType === GridType.GridWithDetail || hasDetailForm) && !isInlineEdit) {
    if (selectedRows && selectedRows.size > 0) {
      const selectedId = [...selectedRows][0];
      const row = displayData.find((r) => String(r.Id) === selectedId);
      if (row && onEditRow) {
        onEditRow(row);
        return { success: true };
      }
    }
    return {
      success: false,
      error: 'Select a row first, then click Edit',
    };
  }

  // Inline editable grids — editing is always enabled
  return {
    success: true,
    message: 'Inline editing enabled',
  };
};

/**
 * Handler for 'save' command
 * Saves all pending changes (modified rows, new rows, deletions)
 */
export const saveCommandHandler: CommandHandler = async (_command, context) => {
  const { onSave, hasUnsavedChanges } = context;

  if (!hasUnsavedChanges) {
    return {
      success: false,
      error: 'No unsaved changes',
    };
  }

  try {
    const saved = await onSave();
    if (!saved) {
      return {
        success: false,
        error: 'Incomplete data. Entry not saved.',
      };
    }
    return {
      success: true,
      message: 'Changes saved successfully',
      shouldRefresh: true,
    };
  } catch (error: unknown) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to save changes',
    };
  }
};

/**
 * Handler for 'delete' command
 * Deletes selected rows (requires row selection)
 */
export const deleteCommandHandler: CommandHandler = (_command, context) => {
  const { selectedRows, displayData, onDeleteRow } = context;

  if (!selectedRows || selectedRows.size === 0) {
    return {
      success: false,
      error: 'Please select a row to delete',
    };
  }

  if (!onDeleteRow) {
    return { success: false, error: 'Delete operation is not available' };
  }

  for (const rowId of selectedRows) {
    const row = displayData?.find((r) => String(r.Id) === rowId);
    if (isBooleanValueTrue(row?.IsAiGenerated)) {
      return {
        success: false,
        error: 'AI-generated worklogs cannot be deleted.',
      };
    }
  }

  for (const rowId of selectedRows) {
    const row = displayData?.find((r) => String(r.Id) === rowId);
    if (row) {
      onDeleteRow(row);
    }
  }

  return {
    success: true,
  };
};

/**
 * Handler for 'deleteentities' command (alias: "custom", action: "deleteEntities")
 * Batch-deletes selected rows after showing a confirmation dialog with the row count.
 */
export const deleteEntitiesHandler: CommandHandler = async (_command, context) => {
  const {
    selectedRows,
    clearSelection,
    metadata,
    displayData,
    deleteBatchRows,
    requestDeleteConfirmation,
  } = context;

  if (!selectedRows || selectedRows.size === 0) {
    return {
      success: false,
      error: 'Please choose at least one row',
    };
  }

  if (!deleteBatchRows) {
    return {
      success: false,
      error: 'Delete operation is not available',
    };
  }

  if (!requestDeleteConfirmation) {
    return {
      success: false,
      error: 'Confirmation dialog is not available',
    };
  }

  const nodeKey = metadata.contentKey;
  const savedIds = Array.from(selectedRows).filter((id) => id && !id.startsWith('temp_'));

  if (savedIds.length === 0) {
    return {
      success: false,
      error: 'No saved rows selected to delete',
    };
  }

  const hasAiGenerated = savedIds.some((rowId) => {
    const row = displayData?.find((r) => String(r.Id) === rowId);
    return isBooleanValueTrue(row?.IsAiGenerated);
  });

  if (hasAiGenerated) {
    return {
      success: false,
      error: 'AI-generated worklogs cannot be deleted.',
    };
  }

  const count = savedIds.length;
  const confirmed = await requestDeleteConfirmation(
    `Are you sure you want to delete ${count} record${count > 1 ? 's' : ''}? This action cannot be undone.`
  );

  if (!confirmed) {
    return { success: false };
  }

  const entities = savedIds.map((id) => ({ Id: id, NodeKey: nodeKey }));

  try {
    await deleteBatchRows(entities);

    clearSelection?.();
    return {
      success: true,
      shouldRefresh: true,
      message: `${count} record${count > 1 ? 's' : ''} deleted`,
    };
  } catch (error: unknown) {
    clearSelection?.();
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to delete records',
    };
  }
};

/**
 * Handler for 'revert' command
 * Cancels all pending changes and reverts to original data
 */
export const revertCommandHandler: CommandHandler = (_command, context) => {
  const { onCancel, hasUnsavedChanges } = context;

  if (!hasUnsavedChanges) {
    return {
      success: false,
      error: 'No changes to revert',
    };
  }

  onCancel();

  return {
    success: true,
    shouldRefresh: false,
  };
};
